import { ApiRegister, ApiRetailerPaymentType } from '@vend/types'
import { RoundedNumber } from '../../../payments/cash-payment-modal/CashPaymentModal'
import { ExternalPaymentData } from '../../../../models/payment'
import { SurchargeTenderProperties } from '../../../../models/sale'
import { EcomRefundModalOptions as EcomRefundProps } from '../../../payments/ecom-refund-modal/helpers/types'
import { PartialPaymentTypeName } from '../../../payments/partial-payment-modal/PartialPaymentModal'

export type PaymentModalType =
  | 'bigcommerceGiftCerts'
  | 'cash'
  | 'cashDiscounting'
  | 'lspayCashDiscounting'
  | 'giftCard'
  | 'integrated'
  | 'lsPay'
  | 'lsPayEcomRefund'
  | 'lspayRecovery'
  | 'partialPayment'
  | 'paymentReference'
  | 'swiper'
  | 'paypalEcomRefund'
  | 'swiperMaintenance'

// Base payment option type that all specific payment options will extend
export interface BasePaymentOption {
  paymentType: ApiRetailerPaymentType
  paymentModalType: PaymentModalType
}

export interface SwiperMaintenancePaymentOption extends BasePaymentOption {
  paymentModalType: 'swiperMaintenance'
  paymentType: ApiRetailerPaymentType
}
// Cash payment option
export interface CashPaymentOption extends BasePaymentOption {
  roundedAmountTendered: RoundedNumber
  roundedBalance: RoundedNumber
  rounding: string
}

export interface CashDiscountingPaymentOption extends BasePaymentOption {
  amountTendered: string | number
}

export interface LspayCashDiscountingPaymentOption extends BasePaymentOption {
  paymentModalType: 'lspayCashDiscounting'
  /** Discounted amount the customer pays (already rounded). */
  roundedAmountTendered: RoundedNumber
  /** Discounted balance (already rounded). */
  roundedBalance: RoundedNumber
  rounding: string
  /** Discount percentage as returned by LSPay, e.g. `4` for 4%. */
  lspCashDiscountPercentage: number
  /** Pre-discount tendered amount; used when the customer removes the discount. */
  originalRoundedAmountTendered: RoundedNumber
  /** Pre-discount balance; used when the customer removes the discount. */
  originalRoundedBalance: RoundedNumber
  /** Pre-tax sale total used to calculate the LSPay cash discount amount. */
  exclusivePrice: number | string
}

export interface IntegratedPaymentOption extends BasePaymentOption {
  amount: string
  registerId: string
  externalTransactionIds?: string[]
  registerSalePaymentIds?: string[]
  paymentId: string
}

export interface GiftCardPaymentOption extends BasePaymentOption {
  amount: string
}

export interface SwiperPaymentOption extends BasePaymentOption {
  amount: number
  registerId: string
  currency: string
}

export interface PartialPaymentOption extends BasePaymentOption {
  paymentTypeName: PartialPaymentTypeName
  availableCredit: number
}

export type LSPayFlow = 'terminal' | 'cnp' | 'pmof'
export interface LSPayPaymentOption extends BasePaymentOption {
  amount: number
  outletId: string
  currency: string
  register: ApiRegister
  registerSaleId: string
  paymentIntentId?: string
  isRecovery: boolean
  transactionId: string
  originalRegisterSalePaymentIds?: string[]
  originalSalePaidByOtherPT?: boolean
  // Total amount from original LSPay payments for split-tender detection
  originalLspayPaymentAmount?: number
  withSurcharge?: boolean
  tipEligibleAmount?: number
  paymentType: ApiRetailerPaymentType
  // Discriminator to route different LSPay flows while keeping the same payment type.
  // 'terminal' => terminal, 'cnp' => manual entry, 'pmof' => payment method on file.
  lspayFlow?: LSPayFlow
  // PMOF specific: the selected method id and customer id
  paymentMethodOnFileId?: string
  customerId?: string
  // Indicates the original payment was processed via Processing Service.
  // Used to route refunds through the legacy flow that supports Processing Service.
  originalSaleHasProcessingServicePayment?: boolean
}

export interface EcomRefundOption extends BasePaymentOption, EcomRefundProps {
  amount: number
  paymentType: ApiRetailerPaymentType
}

export interface PaymentReferencePaymentOption extends BasePaymentOption {
  paymentModalType: 'paymentReference'
  amount: string
}

export interface BigCommergeGiftCertsOption extends BasePaymentOption {
  amount: string
}
export type PaymentOptions =
  | CashPaymentOption
  | CashDiscountingPaymentOption
  | LspayCashDiscountingPaymentOption
  | IntegratedPaymentOption
  | GiftCardPaymentOption
  | LSPayPaymentOption
  | EcomRefundOption
  | SwiperPaymentOption
  | PartialPaymentOption
  | SwiperMaintenancePaymentOption
  | BigCommergeGiftCertsOption
  | PaymentReferencePaymentOption

export const isCashPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is CashPaymentOption => {
  return (
    'roundedAmountTendered' in paymentOptions &&
    paymentOptions.paymentModalType === 'cash'
  )
}

export const isCashDiscountingPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is CashDiscountingPaymentOption => {
  return (
    'amountTendered' in paymentOptions &&
    paymentOptions.paymentModalType === 'cashDiscounting'
  )
}

export const isLspayCashDiscountingPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is LspayCashDiscountingPaymentOption => {
  return paymentOptions.paymentModalType === 'lspayCashDiscounting'
}

export const isLSPayPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is LSPayPaymentOption => {
  return (
    'outletId' in paymentOptions && paymentOptions.paymentModalType === 'lsPay'
  )
}

export const isIntegratedPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is IntegratedPaymentOption => {
  return (
    'registerId' in paymentOptions &&
    paymentOptions.paymentModalType === 'integrated'
  )
}

export const isLSPayEcomRefundPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is EcomRefundOption => {
  return (
    'registerId' in paymentOptions &&
    paymentOptions.paymentModalType === 'lsPayEcomRefund'
  )
}

export const isPaypalEcomRefundPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is EcomRefundOption => {
  return (
    'registerId' in paymentOptions &&
    paymentOptions.paymentModalType === 'paypalEcomRefund'
  )
}

export const isSwiperPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is SwiperPaymentOption => {
  return paymentOptions.paymentModalType === 'swiper'
}

export const isGiftCardPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is GiftCardPaymentOption => {
  return paymentOptions.paymentModalType === 'giftCard'
}

export const isPartialPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is PartialPaymentOption => {
  return (
    'paymentTypeName' in paymentOptions &&
    paymentOptions.paymentModalType === 'partialPayment'
  )
}

export const isPaymentReferencePaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is PaymentReferencePaymentOption => {
  return paymentOptions.paymentModalType === 'paymentReference'
}

export const isBigCommerceGiftCertsPaymentOption = (
  paymentOptions: PaymentOptions
): paymentOptions is BigCommergeGiftCertsOption => {
  return (
    paymentOptions.paymentModalType === 'bigcommerceGiftCerts' &&
    'amount' in paymentOptions
  )
}
/** Common type that after each payment success, it should be converted into this type to be able to complete Sale payment
 * More info can be found in https://github.com/vend/webregister/blob/f8d8a992368700c91474da4a34bee2f0230e2c56/assets/scripts/controllers/current-sale/payment.ts#L998
 */
export interface SalePayment {
  paymentAmount: string | number
  paymentType: ApiRetailerPaymentType
  transactionData?: ExternalPaymentData
  paymentId?: string
  tipAmount?: number | string
  surchargeData?: SurchargeTenderProperties
  changeAmount?: string | number
  paymentReference?: string
}
